import type { RepeatableField } from "@/features/dynamic-variables/repeatable/types";
import Button from "@components/ui/button";
import Dialog from "@components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { ScrollArea } from "@components/ui/scroll-area";
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconDownload,
  IconFileTypeCsv,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useRef, useState } from "react";
import { readStructuredCsvFile } from "../structured-csv";
import {
  createRepeatableCsvExamples,
  createRepeatableCsvTemplate,
  downloadCsv,
  validateRepeatableCsv,
  type RepeatableCsvValidation,
} from "./repeatable-csv";

interface Props {
  /** Group key — used for the downloaded file names. */
  groupKey: string;
  fields: RepeatableField[];
  /** Replaces the group's rows with the imported ones. */
  onImport: (rows: Record<string, string>[]) => void;
}

const MENU_BUTTON_CLASS =
  "tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-shadow-2xs";

/**
 * "Upload" menu for a repeatable group's rows — mirrors the Variables page's
 * CSV menu (custom-variables-header.tsx): Upload CSV, Download blank template,
 * Download example CSV. An upload is reviewed in a dialog before it replaces
 * the rows.
 */
export const RepeatableCsvMenu = ({ groupKey, fields, onImport }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [validation, setValidation] = useState<RepeatableCsvValidation | null>(
    null,
  );
  const [readError, setReadError] = useState<string | null>(null);

  const selectFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = await readStructuredCsvFile(file);
      setReadError(null);
      setValidation(validateRepeatableCsv(parsed, fields));
    } catch (error) {
      setReadError(
        error instanceof Error && error.message
          ? error.message
          : "Could not read the CSV file.",
      );
    }
  };

  const importRows = () => {
    if (!validation || validation.issues.length > 0) return;
    onImport(validation.rows);
    setValidation(null);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <input
        ref={inputRef}
        className="tw-hidden"
        type="file"
        aria-label="Choose CSV file"
        accept=".csv,text/csv,application/csv,application/vnd.ms-excel"
        onChange={(event) => {
          void selectFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={MENU_BUTTON_CLASS}>
            <IconFileTypeCsv className="tw-size-4 !tw-fill-none" stroke={1.5} />
            Upload
            <IconChevronDown className="tw-size-4 !tw-fill-none" stroke={1.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="tw-w-[240px] tw-p-1"
        >
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            <IconUpload className="tw-size-4" stroke={1.5} />
            Upload CSV
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              downloadCsv(
                createRepeatableCsvTemplate(fields),
                `${groupKey}-template.csv`,
              )
            }
          >
            <IconDownload className="tw-size-4" stroke={1.5} />
            Download blank template
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              downloadCsv(
                createRepeatableCsvExamples(fields),
                `${groupKey}-examples.csv`,
              )
            }
          >
            <IconFileTypeCsv className="tw-size-4" stroke={1.5} />
            Download example CSV
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {readError && (
        <span role="alert" className="tw-text-xs tw-text-[#D43548]">
          {readError}
        </span>
      )}
      {/* Mounted only while a file is under review: the app Dialog subscribes
          to the global dialog state, so a permanently mounted closed dialog per
          card would cost a subscription (and demand that store slice) for
          nothing. */}
      {validation && (
        <RepeatableCsvPreviewDialog
          validation={validation}
          fields={fields}
          onClose={() => setValidation(null)}
          onImport={importRows}
        />
      )}
    </div>
  );
};

const RepeatableCsvPreviewDialog = ({
  validation,
  fields,
  onClose,
  onImport,
}: {
  validation: RepeatableCsvValidation;
  fields: RepeatableField[];
  onClose: () => void;
  onImport: () => void;
}) => {
  const rowCount = validation.rows.length;
  const blocked = validation.issues.length > 0 || rowCount === 0;
  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      closeOnOutsideClick
    >
      <Dialog.Content
        aria-label="Review CSV import"
        className="tw-max-h-[80vh] tw-w-[min(720px,calc(100vw-32px))] tw-overflow-y-auto tw-rounded-xl tw-bg-white tw-shadow-lg"
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-[#e7e6ec] tw-px-6 tw-py-5">
          <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-[#12033e]">
            Review CSV import
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="tw-flex tw-size-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-transparent tw-text-[#594f78] hover:tw-bg-[#f3f2f5]"
          >
            <IconX className="tw-size-4" stroke={1.5} />
          </button>
        </div>
        <div className="tw-flex tw-flex-col tw-gap-4 tw-p-6">
          {validation.issues.length > 0 ? (
            <div
              role="alert"
              className="tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-p-4"
            >
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-red-700">
                <IconAlertCircle className="tw-size-4" stroke={1.5} />
                Fix {validation.issues.length} issue
                {validation.issues.length === 1 ? "" : "s"} before importing
              </div>
              <ul className="tw-mb-0 tw-mt-3 tw-space-y-1 tw-pl-5 tw-text-xs tw-leading-5 tw-text-red-700">
                {validation.issues.map((issue) => (
                  <li key={issue.message}>{issue.message}</li>
                ))}
              </ul>
              <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-text-red-700">
                Expected columns: {fields.map((f) => f.key).join(", ")}
              </p>
            </div>
          ) : (
            <div
              role="status"
              className="tw-bg-emerald-50 tw-text-emerald-700 tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-4 tw-py-3 tw-text-sm"
            >
              <IconCheck className="tw-size-4" stroke={1.5} />
              {rowCount} row{rowCount === 1 ? "" : "s"} ready to import - they
              replace the current rows
            </div>
          )}
          {rowCount > 0 && (
            <ScrollArea maxHeight={320}>
              <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-blue-100">
                <div
                  className="tw-grid tw-bg-blue-50 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-blue/60"
                  style={{
                    gridTemplateColumns: `56px repeat(${fields.length}, minmax(0, 1fr))`,
                  }}
                >
                  <span>Row</span>
                  {fields.map((f) => (
                    <span key={f.key} className="tw-truncate" title={f.key}>
                      {f.name}
                    </span>
                  ))}
                </div>
                {validation.rows.map((row, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: preview rows have no id
                    key={index}
                    className="tw-grid tw-items-center tw-border-t tw-border-solid tw-border-blue-100 tw-px-3 tw-py-2.5 tw-text-sm tw-text-blue"
                    style={{
                      gridTemplateColumns: `56px repeat(${fields.length}, minmax(0, 1fr))`,
                    }}
                  >
                    <span className="tw-text-xs tw-text-blue/60">
                      {index + 1}
                    </span>
                    {fields.map((f) => (
                      <span
                        key={f.key}
                        className="tw-min-w-0 tw-truncate"
                        title={row[f.key]}
                      >
                        {row[f.key]}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
        <div className="tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-solid tw-border-[#e7e6ec] tw-px-6 tw-py-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={blocked} onClick={onImport}>
            Import {rowCount} row{rowCount === 1 ? "" : "s"}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
